import { useMemo, useState, type ReactNode } from "react";
import { Badge, Button, Dialog, OverflowMenu, Select, TextField, vars } from "@pack-it-up/ui-web";
import type { ManagedProfile } from "@pack-it-up/core";
import type { ManagedProfileId, UserId } from "@pack-it-up/shared";
import { createInlineEdit, errorToMessage } from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { EditAffordance, editableFieldStyle } from "../shared/ListTopbar.js";
import { editableField, editableHover } from "../shared/ListTopbar.css.js";
import { addGuardianPill } from "./ManagedProfiles.css.js";

// Dialog to upgrade a managed profile to a real account. Per Doc 04 §7 + the T15 note:
// upgrade *links* an existing, email-verified account — it does not create one — so the
// copy makes the create+verify-first prerequisite explicit and surfaces the callable's
// not-found / failed-precondition errors. It also warns about the visibility change.
const UpgradeDialog = ({ profile, onClose }: { profile: ManagedProfile; onClose: () => void }): ReactNode => {
  const { managed } = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const r = await managed.getState().upgradeToAccount(profile.id, email.trim());
    setBusy(false);
    if (r.ok) onClose();
    else setError(errorToMessage(r.error));
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Upgrade ${profile.displayName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={email.trim() === "" || busy}>
            Link account
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: vars.space.md, fontSize: vars.font.sizeMd }}>
        <p style={{ margin: 0, color: vars.color.textMuted }}>
          Have {profile.displayName} <strong>create their own PackItUp account and verify their email first</strong>, then
          enter that email here to link it. This doesn’t create an account.
        </p>
        <p style={{ margin: 0, color: vars.color.textMuted }}>
          Heads up: once linked, they’ll be able to <strong>see the trips they’re part of</strong>.
        </p>
        <TextField label="Their account email" type="email" value={email} onChange={setEmail} />
        {error !== null && (
          <div role="alert" style={{ color: vars.color.danger, fontSize: vars.font.sizeSm }}>
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
};

// Click-to-rename, same interaction as the trip title in `ListTopbar`'s `TopbarTitle`:
// click the name to open a text field with save (✓) / cancel (✕) affordances,
// Enter/blur saves, Esc cancels, empty reverts.
const ProfileName = ({ profile, onRename }: { profile: ManagedProfile; onRename: (name: string) => void }): ReactNode => {
  const edit = useMemo(() => createInlineEdit(), []);
  const mode = useStoreValue(edit, (s) => s.mode);
  const draft = useStoreValue(edit, (s) => s.draft);

  const commit = (): void => {
    const r = edit.getState().commit();
    if (r.kind === "save") onRename(r.value);
  };

  if (mode === "editing") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: vars.space.xs }}>
        <TextField
          label=""
          aria-label={`Rename ${profile.displayName}`}
          value={draft}
          autoFocus
          onChange={(v) => edit.getState().setDraft(v)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") edit.getState().cancel();
          }}
        />
        <EditAffordance onSave={commit} onCancel={() => edit.getState().cancel()} />
      </span>
    );
  }
  return (
    <button
      type="button"
      className={[editableField, editableHover].join(" ")}
      onClick={() => edit.getState().beginEdit(profile.id, profile.displayName)}
      style={{
        ...editableFieldStyle,
        fontWeight: vars.font.weightMedium,
        fontFamily: "inherit",
        fontSize: "inherit",
      }}
    >
      {profile.displayName}
    </button>
  );
};

const ProfileCard = ({ profile, meId }: { profile: ManagedProfile; meId: UserId }): ReactNode => {
  const { managed, friends } = useSession();
  const myFriends = useStoreValue(friends, (s) => s.friends);
  const [upgrading, setUpgrading] = useState(false);
  // The friend picker stays collapsed behind the "+" pill until clicked (it shouldn't
  // sit open all the time), and closes again after a successful add — clicking "+"
  // again re-opens it with the updated (now one-shorter) friend list, so adding several
  // guardians in a row is just repeated +/pick/Add rather than one dropdown that runs
  // out and disappears for good.
  const [addingGuardian, setAddingGuardian] = useState(false);

  const nameFor = (uid: UserId): string =>
    uid === meId ? "You" : (myFriends.find((f) => f.userId === uid)?.displayName ?? "Guardian");
  const addable = myFriends.filter((f) => !profile.guardianUserIds.includes(f.userId));
  const upgraded = profile.upgradedToUserId !== undefined;
  const canAdd = addable.length > 0 && !upgraded;

  return (
    <div style={{ border: `1px solid ${vars.color.border}`, borderRadius: vars.radius.md, padding: vars.space.md, display: "flex", flexDirection: "column", gap: vars.space.sm }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <ProfileName
          profile={profile}
          onRename={(name) => void managed.getState().updateProfile({ id: profile.id, displayName: name })}
        />
        {upgraded ? (
          <Badge tone="accentSoft">upgraded</Badge>
        ) : (
          <OverflowMenu
            label={`More options for ${profile.displayName}`}
            items={[{ label: "Upgrade", onClick: () => setUpgrading(true) }]}
          />
        )}
      </div>

      <div style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>Guardians</div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: vars.space.xs }}>
        {profile.guardianUserIds.map((uid) => (
          <span key={uid} style={{ display: "inline-flex", alignItems: "center", gap: vars.space.xs }}>
            <Badge>{nameFor(uid)}</Badge>
            {profile.guardianUserIds.length > 1 && (
              <button
                aria-label={`Remove guardian ${nameFor(uid)}`}
                onClick={() => void managed.getState().removeGuardian(profile.id, uid)}
                style={{ border: "none", background: "transparent", color: vars.color.textMuted, cursor: "pointer" }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {canAdd && !addingGuardian && (
          <button type="button" aria-label="Add a guardian" onClick={() => setAddingGuardian(true)} className={addGuardianPill}>
            +
          </button>
        )}
      </div>
      {canAdd && addingGuardian && (
        <AddGuardianForm
          profileId={profile.id}
          options={addable.map((f) => ({ value: f.userId, label: f.displayName }))}
          onDone={() => setAddingGuardian(false)}
        />
      )}

      {upgrading && <UpgradeDialog profile={profile} onClose={() => setUpgrading(false)} />}
    </div>
  );
};

// The guardian picker itself — a Select + Add + Cancel row, shown below the guardian
// pills once the "+" pill (in `ProfileCard`) is clicked. `onDone` closes it back to the
// pill, whether the picker saved or was cancelled.
const AddGuardianForm = ({ profileId, options, onDone }: { profileId: ManagedProfileId; options: { value: string; label: string }[]; onDone: () => void }): ReactNode => {
  const { managed } = useSession();
  const [value, setValue] = useState("");

  return (
    <div style={{ display: "flex", gap: vars.space.sm, alignItems: "flex-end" }}>
      <div style={{ flex: 1 }}>
        <Select label="Add a guardian" value={value} onChange={setValue} options={[{ value: "", label: "Choose a friend…" }, ...options]} />
      </div>
      <Button
        disabled={value === ""}
        onClick={async () => {
          await managed.getState().addGuardian(profileId, value as UserId);
          onDone();
        }}
      >
        Add
      </Button>
      <Button variant="ghost" onClick={onDone}>
        Cancel
      </Button>
    </div>
  );
};

// Managed profiles tab on the Friends page (Doc 04 §7): list, create, guardians, upgrade.
export const ManagedProfiles = (): ReactNode => {
  const { auth } = useStores();
  const { managed } = useSession();
  const meId = useStoreValue(auth, (s) => s.user?.id ?? null);
  const profiles = useStoreValue(managed, (s) => s.profiles);
  const [name, setName] = useState("");

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: vars.space.md }}>
      <p style={{ margin: 0, fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>
        Profiles for people without their own account (e.g. kids). You can add them to trips and pack for them.
      </p>

      <div style={{ display: "flex", gap: vars.space.sm, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <TextField label="New profile name" value={name} onChange={setName} placeholder="e.g. Sam" />
        </div>
        <Button
          disabled={name.trim() === ""}
          onClick={async () => {
            await managed.getState().createProfile(name.trim());
            setName("");
          }}
        >
          Create
        </Button>
      </div>

      {profiles.map((p) => (meId !== null ? <ProfileCard key={p.id} profile={p} meId={meId} /> : null))}
    </section>
  );
};
